import { activityList } from 'api/activity/activity.js';
import BreadCrumb from 'components/common/BreadCrumb.js';
import { DataTable } from 'components/common/DataTable.js';
import NoDataFound from 'components/common/NoDataFound.js';
import SearchInput from 'components/common/SearchInput.js';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Container, Input } from 'reactstrap';
import { getNotificationDescription, getNotificationIcon, getNotificationTitle } from './notificationHelpers.js';

// "lock_status" -> "Lock Status", "MASTER_CODE" -> "Master Code"
const formatLabel = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value)
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Device states arrive as codes ("LOCKED", "LOCKED_COMPARTMENT", "true") -> "Locked",
// "Locked Compartment", "True". Free-text values (Wi-Fi name, battery %, weight) are kept as-is.
const formatState = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value);
  const isCode = /^[A-Z0-9_]+$/.test(text) || /^(true|false)$/i.test(text);
  return isCode && /[A-Za-z]/.test(text) ? formatLabel(text) : text;
};

const WRAP_TEXT = { whiteSpace: 'normal', overflowWrap: 'anywhere' };

const NotificationTitle = ({ row }) => (
  <div className="d-flex align-items-center py-2" style={{ gap: '0.6rem' }}>
    <div
      className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 30, height: 30 }}
    >
      <i className={getNotificationIcon(row)} style={{ fontSize: 16 }} />
    </div>
    <div style={{ minWidth: 0, ...WRAP_TEXT }}>
      <div className="fw-medium text-nowrap">{getNotificationTitle(row)}</div>
      <div className="text-muted fs-11">{formatLabel(row.action) || 'N/A'}</div>
    </div>
  </div>
);

// Skeleton widths per column, matching the table layout below
const SKELETON_ROW_COUNT = 8;

const SkeletonLine = ({ width, className = '' }) => (
  <span className={`placeholder rounded d-block ${className}`} style={{ width, height: 12 }} />
);

const NotificationLogsSkeleton = ({ rows = SKELETON_ROW_COUNT }) => (
  <div className="placeholder-glow bg-white w-100" aria-busy="true" aria-label="Loading notification logs">
    <div className="d-flex align-items-center px-3 py-3 border-bottom" style={{ gap: '1.5rem' }}>
      {['12%', '26%', '10%', '12%', '14%', '12%'].map((width, idx) => (
        <SkeletonLine key={idx} width={width} />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, idx) => (
      <div key={idx} className="d-flex align-items-center px-3 py-3 border-bottom" style={{ gap: '1.5rem' }}>
        <div className="d-flex align-items-center flex-shrink-0" style={{ width: '18%', gap: '0.6rem' }}>
          <span className="placeholder rounded-circle flex-shrink-0" style={{ width: 30, height: 30 }} />
          <div className="flex-grow-1">
            <SkeletonLine width="75%" className="mb-2" />
            <SkeletonLine width="45%" />
          </div>
        </div>
        <div style={{ width: '28%' }}>
          <SkeletonLine width="95%" className="mb-2" />
          <SkeletonLine width="60%" />
        </div>
        <div style={{ width: '11%' }}>
          <SkeletonLine width="80%" />
        </div>
        <div style={{ width: '12%' }}>
          <SkeletonLine width="75%" />
        </div>
        <div style={{ width: '16%' }}>
          <SkeletonLine width="70%" className="mb-2" />
          <SkeletonLine width="90%" />
        </div>
        <div style={{ width: '13%' }}>
          <SkeletonLine width="85%" />
        </div>
      </div>
    ))}
  </div>
);

const getPerformedBy = (row) => {
  if (row.userDetails) {
    return { type: 'User', name: row.userDetails.name, email: row.userDetails.email };
  }
  if (row.courierDetails) {
    return { type: row.courierDetails.name, name: 'Delivery Partner', email: row.courierDetails.email };
  }
  return null;
};

// Weight and parcel logs report weights in grams
const WEIGHT_ACTIONS = ['weight', 'parcel'];

const formatStateValue = (log, value) => {
  const text = formatState(value);
  const isWeight = WEIGHT_ACTIONS.includes(String(log?.action || '').toLowerCase());
  return isWeight && text !== null && /^-?\d+(\.\d+)?$/.test(text) ? `${text} gm` : text;
};

const DetailItem = ({ label, value }) => (
  <div className="col-md-3 col-sm-6 mb-2">
    <div className="text-muted fs-12">{label}</div>
    <div className="fw-medium">{value || 'N/A'}</div>
  </div>
);

const ExpandedLogDetails = ({ data }) => {
  const user = data.userDetails;
  const courier = data.courierDetails;
  const owner = data.deviceOwnerDetails;

  return (
    <div className="p-3 bg-light border-bottom">
      <h6 className="mb-2">Log Details</h6>
      <div className="row">
        <DetailItem label="Log ID" value={data.id} />
        <DetailItem label="Notification" value={getNotificationTitle(data)} />
        <DetailItem label="Device" value={data.device_alias_name} />
        <DetailItem label="Action" value={formatLabel(data.action)} />
        <DetailItem label="Triggered Through" value={formatLabel(data.device_opened_closed_through)} />
        <DetailItem label="Previous State" value={formatStateValue(data, data.previous_data)} />
        <DetailItem label="Current State" value={formatStateValue(data, data.current_data)} />
        <DetailItem label="Date & Time" value={dayjs(data.created_at).format('YYYY-MM-DD hh:mm:ss A')} />
      </div>

      <h6 className="mt-3 mb-2">Device Owner</h6>
      <div className="row">
        <DetailItem label="Owner Name" value={owner?.name} />
        <DetailItem label="Owner Email" value={owner?.email} />
      </div>

      {user && (
        <>
          <h6 className="mt-3 mb-2">User Details</h6>
          <div className="row">
            <DetailItem label="User ID" value={user.id} />
            <DetailItem label="Name" value={user.name} />
            <DetailItem label="Email" value={user.email} />
            <DetailItem label="Address" value={user.address} />
            <DetailItem label="Date Of Birth" value={user.date_of_birth ? dayjs(user.date_of_birth).format('YYYY-MM-DD') : null} />
            <DetailItem label="Verified" value={user.is_verified ? 'Yes' : 'No'} />
            <DetailItem label="Status" value={user.status} />
          </div>
        </>
      )}

      {courier && (
        <>
          <h6 className="mt-3 mb-2">Delivery Partner Details</h6>
          <div className="row">
            <DetailItem label="Category Name" value={courier.name} />
            <DetailItem label="Delivery Code" value={courier.delivery_code} />
          </div>
        </>
      )}
    </div>
  );
};

// Values accepted by the admin API's `actionFields` filter (AllowedActivityActionFilters)
const NOTIFICATION_TYPE_OPTIONS = [
  { value: 'lock_status', label: 'Lock / Unlock' },
  { value: 'parcel', label: 'Parcel' },
  { value: 'parcel_delivery_aborted', label: 'Parcel Delivery Aborted' },
  { value: 'weight', label: 'Weight Changed' },
  { value: 'battery', label: 'Battery (all)' },
  { value: 'primary_battery_level', label: 'Primary Battery Level' },
  { value: 'secondary_battery_level', label: 'Backup Battery Level' },
  { value: 'primary_low_battery', label: 'Primary Low Battery' },
  { value: 'secondary_low_battery', label: 'Backup Low Battery' },
  { value: 'tare_calibration', label: 'Calibration' },
  { value: 'keypad_blocked', label: 'Device Blocked' }
];

const DEFAULT_SORT = { order: 'created_at', direction: 'desc' };

export default function NotificationLogs () {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ total: 0, perPage: 10, currentPage: 1 });
  const [search, setSearch] = useState('');
  const [notificationType, setNotificationType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [searchInputKey, setSearchInputKey] = useState(0);
  const latestRequestId = useRef(0);

  const hasActiveFilters = Boolean(search || notificationType || startDate || endDate);

  useEffect(() => {
    getActivityList({
      page: meta.currentPage,
      perPage: meta.perPage,
      search,
      notificationType,
      startDate,
      endDate,
      order: sort.order,
      direction: sort.direction
    });
  }, [meta.currentPage, meta.perPage, search, notificationType, startDate, endDate, sort]);

  async function getActivityList ({ page = 1, perPage = 10, search = '', notificationType = '', startDate = '', endDate = '', order = '', direction = '' }) {
    // Ignore responses from older requests when filters change quickly
    const requestId = ++latestRequestId.current;
    try {
      setLoading(true);
      const params = new URLSearchParams();

      params.append('page', page);
      params.append('perPage', perPage);
      if (search?.trim()) {
        params.append('search', search.trim());
      }
      if (notificationType) {
        params.append('actionFields', notificationType);
      }
      // Plain YYYY-MM-DD dates; the API reads them as whole days in the browser's timezone
      // (endDate is inclusive) using timezoneOffset, e.g. -330 for IST.
      if (startDate) {
        params.append('startDate', startDate);
      }
      if (endDate) {
        params.append('endDate', endDate);
      }
      if (startDate || endDate) {
        params.append('timezoneOffset', new Date().getTimezoneOffset());
      }
      if (order) {
        params.append('order', order);
      }
      if (direction) {
        params.append('direction', direction);
      }
      const res = await activityList(`?${params.toString()}`);
      if (requestId !== latestRequestId.current) return;
      // Admin API returns { device_logs, meta, message } at the top level.
      const result = res?.device_logs ? res : res?.data;

      setLogs(result?.device_logs || []);
      setMeta({
        total: result?.meta?.total || 0,
        perPage: result?.meta?.perPage || perPage,
        currentPage: result?.meta?.currentPage || page
      });
    } catch (error) {
      if (requestId !== latestRequestId.current) return;
      toast.error(error?.response?.data?.status?.message || error?.response?.data?.message || 'Failed to fetch notification logs');
    } finally {
      if (requestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }

  // Any search/filter/sort change starts again from page 1
  const goToFirstPage = () => {
    setMeta((prev) => ({ ...prev, currentPage: 1 }));
    setResetPaginationToggle((prev) => !prev);
  };

  // Must keep the same identity across renders: SearchInput rebuilds its debounce whenever
  // onSearch changes, which otherwise fires a request for every keystroke.
  const handleSearch = useCallback((value) => {
    setSearch(value);
    setMeta((prev) => ({ ...prev, currentPage: 1 }));
    setResetPaginationToggle((prev) => !prev);
  }, []);

  const handleNotificationTypeChange = (e) => {
    setNotificationType(e.target.value);
    goToFirstPage();
  };

  // Keep the range valid (the API rejects startDate after endDate)
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setStartDate(value);
    if (value && endDate && value > endDate) {
      setEndDate(value);
    }
    goToFirstPage();
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDate(value);
    if (value && startDate && value < startDate) {
      setStartDate(value);
    }
    goToFirstPage();
  };

  const handleSort = (column, sortDirection) => {
    const order = column.sortField || DEFAULT_SORT.order;
    const direction = sortDirection || DEFAULT_SORT.direction;
    if (order === sort.order && direction === sort.direction) return;
    setSort({ order, direction });
    goToFirstPage();
  };

  const handleClearFilters = () => {
    setSearch('');
    setNotificationType('');
    setStartDate('');
    setEndDate('');
    setSearchInputKey((prev) => prev + 1);
    goToFirstPage();
  };

  const handlePageChange = (page) => {
    setMeta((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setMeta((prev) => ({ ...prev, currentPage: page, perPage: newPerPage }));
  };

  const columns = [
    {
      name: 'Notification',
      selector: row => row.action,
      cell: row => <NotificationTitle row={row} />,
      grow: 1.3,
      minWidth: '190px'
    },
    {
      name: 'Description',
      cell: row => <div className="py-2" style={{ whiteSpace: 'normal' }}>{getNotificationDescription(row)}</div>,
      grow: 2.2,
      minWidth: '220px'
    },
    {
      name: 'Device',
      selector: row => row.device_alias_name,
      cell: row => <span style={WRAP_TEXT}>{row.device_alias_name || 'N/A'}</span>,
      minWidth: '125px'
    },
    {
      name: 'Device Owner',
      selector: row => row.deviceOwnerDetails?.name,
      cell: row => <span style={WRAP_TEXT}>{row.deviceOwnerDetails?.name || 'N/A'}</span>,
      minWidth: '110px'
    },
    {
      name: 'Performed By',
      cell: row => {
        const performer = getPerformedBy(row);
        if (!performer) return <span >Through Device</span>;
        return (
          <div className="py-1" style={{ minWidth: 0, maxWidth: '100%' }}>
            <div style={WRAP_TEXT}>{performer.name} <span>({performer.type})</span></div>
            {performer.email && <div className="fs-12 text-truncate" title={performer.email}>{performer.email}</div>}
          </div>
        );
      },
      grow: 1.2,
      minWidth: '150px'
    },
    {
      id: 'created_at',
      name: 'Date & Time',
      sortField: 'created_at',
      sortable: true,
      selector: row => row.created_at,
      cell: row => (
        <div className="py-1 text-nowrap">
          <div>{dayjs(row.created_at).format('YYYY-MM-DD')}</div>
          <div className="fs-12">{dayjs(row.created_at).format('hh:mm:ss A')}</div>
        </div>
      ),
      minWidth: '115px'
    }
  ];

  const today = dayjs().format('YYYY-MM-DD');

  document.title = 'Notification Logs';

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mainPageHeader mb-3 text-nowrap">
          <BreadCrumb title="Notification Logs" />
        </div>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
          <div
            style={{ flex: '1 1 220px', maxWidth: '280px' }}
            title="Search by notification (e.g. Door Locked), device name or serial, user or owner name/email, courier, delivery code, Wi-Fi name, trigger (e.g. master code) or log ID"
          >
            <SearchInput
              key={searchInputKey}
              onSearch={handleSearch}
              placeholder="Search logs..."
              inputStyle={{ width: '100%' }}
            />
          </div>
          <Input
            type="select"
            value={notificationType}
            onChange={handleNotificationTypeChange}
            style={{ flex: '0 1 210px', minWidth: '180px' }}
            aria-label="Filter by notification type"
          >
            <option value="">All notification types</option>
            {NOTIFICATION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Input>
          <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '6px' }}>
            <Input
              type="date"
              value={startDate}
              max={endDate || today}
              onChange={handleStartDateChange}
              style={{ width: '150px' }}
              title="From date"
              aria-label="From date"
            />
            <span className="text-muted">to</span>
            <Input
              type="date"
              value={endDate}
              min={startDate || undefined}
              max={today}
              onChange={handleEndDateChange}
              style={{ width: '150px' }}
              title="To date"
              aria-label="To date"
            />
          </div>
          {hasActiveFilters && (
            <Button color="link" className="px-1 text-nowrap" onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
        <div className="accordionTable my-3" style={{
          width: "100%",
          overflow: 'visible',
          position: 'relative',
          zIndex: 1
        }}>
          {
            logs.length > 0 && (
              <DataTable
                columns={columns}
                data={logs}
                progressPending={loading}
                progressComponent={<NotificationLogsSkeleton rows={Math.min(meta.perPage, SKELETON_ROW_COUNT)} />}
                expandableRows
                expandableRowsComponent={ExpandedLogDetails}
                pagination
                paginationServer
                paginationPerPage={meta.perPage}
                paginationRowsPerPageOptions={[10, 15, 25, 30]}
                paginationTotalRows={meta.total}
                paginationDefaultPage={meta.currentPage}
                paginationResetDefaultPage={resetPaginationToggle}
                sortServer
                onSort={handleSort}
                defaultSortFieldId="created_at"
                defaultSortAsc={sort.direction === 'asc'}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
              />
            )
          }
          {logs.length === 0 && loading && <NotificationLogsSkeleton />}
          {logs.length === 0 && !loading && (
            <NoDataFound message={hasActiveFilters ? 'No notification logs match your filters' : 'No notification logs available'} />
          )}
        </div>
      </Container>
    </div>
  );
}
